import React, { useState, useEffect, useMemo } from "react";
import { api } from "../api";

const FISCAL_MONTHS = [
  "April","May","June","July","August","September",
  "October","November","December","January","February","March",
];

const BUDGET_DIMS = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"];

const COMPUTED_ACCT_NUMS = new Set(["60010", "60022", "60100", "60120", "60130"]);

interface YtgAccount { num: string; label: string; group: string }

const YTG_GROUPS: { key: string; label: string; accounts: YtgAccount[] }[] = [
  {
    key: "ft", label: "Full-Time Salaries",
    accounts: [
      { num: "60010", label: "Salaries", group: "ft" },
      { num: "60022", label: "Incentive Bonus (BIP)", group: "ft" },
      { num: "60100", label: "Insurance Benefits", group: "ft" },
      { num: "60120", label: "Payroll Tax", group: "ft" },
      { num: "60130", label: "Retirement Plans", group: "ft" },
    ],
  },
  {
    key: "contract", label: "Contingent / Contract",
    accounts: [
      { num: "60020", label: "Outside Services", group: "contract" },
      { num: "60770", label: "Subcontracted Work", group: "contract" },
    ],
  },
  {
    key: "medc", label: "MEDC",
    accounts: [
      { num: "60943", label: "MEDC Allocation", group: "medc" },
    ],
  },
];

const ALL_YTG_ACCOUNTS = YTG_GROUPS.flatMap(g => g.accounts);

function dimHasUploadedData(dimData: Record<string, any[]>, autoCompute: boolean): boolean {
  return Object.entries(dimData).some(([account, entries]) => {
    const num = account.split(" - ")[0].trim();
    if (autoCompute && COMPUTED_ACCT_NUMS.has(num)) return false;
    return entries.some((e: any) => (e.budget ?? 0) !== 0);
  });
}

interface YTGSimulationStripProps {
  costCenterNumber: string;
}

const fmt = (n: number) => {
  if (n === 0) return "$0";
  const abs = Math.round(Math.abs(n));
  const s = "$" + abs.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return n < 0 ? `(${s})` : s;
};

interface ComputedAccount {
  account: string;
  label: string;
  months: Record<string, number>;
  total: number;
}

function computeContractorMonthly(
  annualSalary: number,
  hire_month: number,
  contract_end_month: number,
  status: string,
  termination_month: number,
  hours_per_week: number
): Record<string, number> {
  const isTurnkey = hours_per_week === 0;
  const result: Record<string, number> = {};

  if (isTurnkey) {
    let activeCount = 0;
    FISCAL_MONTHS.forEach((_, i) => {
      const fm = i + 1;
      const start = hire_month > 0 ? hire_month : 1;
      const end = (status === "terminated" && termination_month > 0)
        ? termination_month
        : (contract_end_month > 0 ? contract_end_month : 12);
      if (fm >= start && fm <= end) activeCount++;
    });
    const perMonth = activeCount > 0 ? Math.round(annualSalary / activeCount) : 0;
    FISCAL_MONTHS.forEach((m, i) => {
      const fm = i + 1;
      const start = hire_month > 0 ? hire_month : 1;
      const end = (status === "terminated" && termination_month > 0)
        ? termination_month
        : (contract_end_month > 0 ? contract_end_month : 12);
      result[m] = (fm >= start && fm <= end) ? perMonth : 0;
    });
  } else {
    FISCAL_MONTHS.forEach((m, i) => {
      const fm = i + 1;
      if (hire_month > 0 && fm < hire_month) {
        result[m] = 0;
      } else if (contract_end_month > 0 && fm > contract_end_month) {
        result[m] = 0;
      } else if (status === "terminated" && termination_month > 0 && fm > termination_month) {
        result[m] = 0;
      } else {
        result[m] = Math.round(annualSalary / 12);
      }
    });
  }
  return result;
}

const YTGSimulationStrip: React.FC<YTGSimulationStripProps> = ({ costCenterNumber }) => {
  const [budgetsByDim, setBudgetsByDim] = useState<Record<string, Record<string, any[]>>>({});
  const [actualsRaw, setActualsRaw] = useState<Record<string, any[]>>({});
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [salaryAutoCompute, setSalaryAutoCompute] = useState(true);
  const [salaryComputed, setSalaryComputed] = useState<ComputedAccount[]>([]);
  const [contractorMonthlyTotals, setContractorMonthlyTotals] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!costCenterNumber) return;
    setLoading(true);
    Promise.all([
      ...BUDGET_DIMS.map(dim =>
        api.get("/budgets", { params: { cost_center: costCenterNumber, dimension: dim } })
      ),
      api.get("/budgets", { params: { cost_center: costCenterNumber, dimension: "Actuals" } }),
      api.get("/budgets/months-with-actuals", { params: { cost_center: costCenterNumber } }),
      api.get("/settings/forecast-config", { params: { cc: costCenterNumber } }),
      api.get(`/salary/computed?cost_center=${costCenterNumber}`),
      api.get(`/salary/employees?cost_center=${costCenterNumber}`),
    ])
      .then(responses => {
        const dimMap: Record<string, Record<string, any[]>> = {};
        BUDGET_DIMS.forEach((dim, i) => { dimMap[dim] = responses[i].data; });
        setBudgetsByDim(dimMap);
        setActualsRaw(responses[BUDGET_DIMS.length].data);
        const months: string[] = responses[BUDGET_DIMS.length + 1].data.months || [];
        setAvailableMonths(months);
        setSalaryAutoCompute(responses[BUDGET_DIMS.length + 2].data.salary_auto_compute !== false);

        const computed: ComputedAccount[] = responses[BUDGET_DIMS.length + 3].data || [];
        setSalaryComputed(computed);

        const employees: any[] = responses[BUDGET_DIMS.length + 4].data || [];
        const temps = employees.filter((e: any) => e.employment_type === "Temp");
        const cTotals: Record<string, number> = {};
        FISCAL_MONTHS.forEach(m => { cTotals[m] = 0; });
        temps.forEach((emp: any) => {
          const monthly = computeContractorMonthly(
            emp.annual_salary || 0,
            emp.hire_month || 0,
            emp.contract_end_month || 0,
            emp.status || "active",
            emp.termination_month || 0,
            emp.hours_per_week ?? 0
          );
          FISCAL_MONTHS.forEach(m => { cTotals[m] += monthly[m]; });
        });
        setContractorMonthlyTotals(cTotals);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [costCenterNumber]);

  const accountData = useMemo(() => {
    if (loading) return [];

    const latestActuals = availableMonths.length > 0 ? availableMonths[availableMonths.length - 1] : "";
    const latestIdx = FISCAL_MONTHS.indexOf(latestActuals);
    let targetDim: string;
    if (latestIdx <= 2)       targetDim = "July Forecast";
    else if (latestIdx <= 5)  targetDim = "October Forecast";
    else                      targetDim = "January Forecast";
    const fallbackChain = [targetDim, "July Forecast", "Annual Budget"];
    let latestDim = "Annual Budget";
    for (const dim of fallbackChain) {
      if (dimHasUploadedData(budgetsByDim[dim] ?? {}, salaryAutoCompute)) {
        latestDim = dim;
        break;
      }
    }

    const allAccounts = Array.from(new Set(
      Object.values(budgetsByDim).flatMap(d => Object.keys(d))
    ));
    const actualsSet = new Set(availableMonths);
    const latestData = budgetsByDim[latestDim] ?? {};
    const abData = budgetsByDim["Annual Budget"] ?? {};

    const salaryByAcct: Record<string, Record<string, number>> = {};
    salaryComputed.forEach(c => { salaryByAcct[c.account] = c.months; });

    return ALL_YTG_ACCOUNTS.map(ytgAcct => {
      const matchingAcct = allAccounts.find(a => a.split(" - ")[0].trim() === ytgAcct.num);

      let annualBudget = 0;
      let ytdActuals = 0;
      let plannedRemaining = 0;

      const hasSalarySimulation = COMPUTED_ACCT_NUMS.has(ytgAcct.num) && salaryByAcct[ytgAcct.num];
      const isContractorAcct = ytgAcct.num === "60020" || ytgAcct.num === "60770";

      for (const month of FISCAL_MONTHS) {
        if (matchingAcct) {
          const abEntry = abData[matchingAcct]?.find((e: any) => e.month === month);
          annualBudget += Number(abEntry?.budget) || 0;
        }

        const hasActual = actualsSet.has(month);

        if (hasActual && matchingAcct) {
          const ae = actualsRaw[matchingAcct]?.find((e: any) => e.month === month);
          ytdActuals += Number(ae?.budget) || 0;
        } else {
          if (hasSalarySimulation) {
            plannedRemaining += salaryByAcct[ytgAcct.num][month] || 0;
          } else if (isContractorAcct && ytgAcct.num === "60020") {
            plannedRemaining += contractorMonthlyTotals[month] || 0;
          } else if (matchingAcct) {
            const entry = latestData[matchingAcct]?.find((e: any) => e.month === month)
              || abData[matchingAcct]?.find((e: any) => e.month === month);
            plannedRemaining += Number(entry?.budget) || 0;
          }
        }
      }

      const mpy = ytdActuals + plannedRemaining;
      const variance = annualBudget - mpy;

      return { ...ytgAcct, annualBudget, mpy, variance };
    });
  }, [budgetsByDim, actualsRaw, availableMonths, salaryAutoCompute, salaryComputed, contractorMonthlyTotals, loading]);

  const totals = useMemo(() => {
    return accountData.reduce(
      (acc, row) => ({
        annualBudget: acc.annualBudget + row.annualBudget,
        mpy: acc.mpy + row.mpy,
        variance: acc.variance + row.variance,
      }),
      { annualBudget: 0, mpy: 0, variance: 0 }
    );
  }, [accountData]);

  if (loading) return null;

  const hasData = accountData.some(r => r.annualBudget !== 0 || r.mpy !== 0);
  if (!hasData) return null;

  return (
    <div style={{
      margin: "20px 0 0",
      borderRadius: 8,
      overflow: "hidden",
      boxShadow: "0 1px 4px oklch(0.15 0.02 250 / 0.10)",
      border: "1.5px solid oklch(0.78 0.04 250)",
      marginBottom: 20,
      background: "oklch(0.94 0.015 250)",
    }}>
      {/* Dark header bar */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "9px 14px", cursor: "pointer",
          background: "oklch(0.22 0.04 250)",
          userSelect: "none",
        }}
      >
        <span className="material-icons" style={{ fontSize: 15, color: "oklch(0.65 0.06 250)", transition: "transform 0.15s", transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>expand_more</span>
        <span style={{ fontWeight: 700, fontSize: 11, color: "oklch(0.92 0.01 250)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          MPY HC Simulation
        </span>
        <span style={{ fontSize: 9, fontWeight: 700, color: "oklch(0.80 0.18 155)", background: "oklch(0.28 0.05 155)", padding: "2px 8px", borderRadius: 3, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Live
        </span>
        {collapsed && (
          <span style={{ fontSize: 11, color: "oklch(0.68 0.02 250)", fontWeight: 500, marginLeft: 12 }}>
            Simulated MPY {fmt(totals.mpy)} &middot; Variance <span style={{ color: totals.variance >= 0 ? "oklch(0.65 0.12 155)" : "oklch(0.65 0.15 25)", fontWeight: 700 }}>{fmt(totals.variance)}</span>
          </span>
        )}
      </div>

      {!collapsed && (
        <>
          {/* Instruction bar */}
          <div style={{ padding: "6px 14px", fontSize: 10, color: "oklch(0.42 0.03 250)", lineHeight: 1.5, background: "oklch(0.92 0.02 250)", borderBottom: "1px solid oklch(0.88 0.025 250)" }}>
            MPY (Most Probable Year-end) = YTD Actuals + Planned Remaining from Salary &amp; Contract tabs. Variance = Budget &minus; MPY.
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
              <thead>
                <tr>
                  {[{ label: "Account", align: "left" }, { label: "Annual Budget", align: "right" }, { label: "Simulated MPY", align: "right" }, { label: "Variance", align: "right" }].map(h => (
                    <th key={h.label} style={{ textAlign: h.align as any, padding: "6px 14px", fontWeight: 700, color: "oklch(0.35 0.03 250)", fontSize: 9, textTransform: "uppercase" as const, letterSpacing: "0.06em", background: "oklch(0.90 0.02 250)", borderBottom: "1px solid oklch(0.85 0.03 250)" }}>{h.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {YTG_GROUPS.map(group => {
                  const groupRows = accountData.filter(r => group.accounts.some(a => a.num === r.num));
                  const groupTotals = groupRows.reduce(
                    (acc, r) => ({ annualBudget: acc.annualBudget + r.annualBudget, mpy: acc.mpy + r.mpy, variance: acc.variance + r.variance }),
                    { annualBudget: 0, mpy: 0, variance: 0 }
                  );
                  return (
                    <React.Fragment key={group.key}>
                      {/* Group header */}
                      <tr style={{ background: "oklch(0.88 0.02 250)" }}>
                        <td colSpan={4} style={{ padding: "5px 14px", fontWeight: 800, fontSize: 9, color: "oklch(0.30 0.04 250)", textTransform: "uppercase" as const, letterSpacing: "0.07em" }}>
                          {group.label}
                        </td>
                      </tr>
                      {/* Account rows */}
                      {groupRows.map(row => {
                        const overBudget = row.variance < 0;
                        return (
                          <tr key={row.num} style={{ borderBottom: "1px solid oklch(0.92 0.015 250)" }}>
                            <td style={{ padding: "5px 14px 5px 28px", fontWeight: 500, color: "oklch(0.20 0.03 250)" }}>
                              <span style={{ fontFamily: "monospace", fontSize: 10, color: "oklch(0.48 0.03 250)", marginRight: 8 }}>{row.num}</span>
                              {row.label}
                            </td>
                            <td style={{ textAlign: "right", padding: "5px 14px", color: "oklch(0.28 0.02 250)" }}>{fmt(row.annualBudget)}</td>
                            <td style={{ textAlign: "right", padding: "5px 14px", fontWeight: 700, color: "oklch(0.16 0.03 250)" }}>{fmt(row.mpy)}</td>
                            <td style={{
                              textAlign: "right", padding: "5px 14px", fontWeight: 700,
                              color: overBudget ? "oklch(0.48 0.20 25)" : "oklch(0.38 0.15 155)",
                            }}>{fmt(row.variance)}</td>
                          </tr>
                        );
                      })}
                      {/* Group subtotal */}
                      {group.accounts.length > 1 && (
                        <tr style={{ background: "oklch(0.94 0.015 250)", borderBottom: "2px solid oklch(0.85 0.03 250)" }}>
                          <td style={{ padding: "5px 14px 5px 28px", fontWeight: 700, fontSize: 11, color: "oklch(0.30 0.05 250)" }}>
                            Subtotal {group.label}
                          </td>
                          <td style={{ textAlign: "right", padding: "5px 14px", fontWeight: 700, color: "oklch(0.30 0.03 250)" }}>{fmt(groupTotals.annualBudget)}</td>
                          <td style={{ textAlign: "right", padding: "5px 14px", fontWeight: 800, color: "oklch(0.18 0.03 250)" }}>{fmt(groupTotals.mpy)}</td>
                          <td style={{
                            textAlign: "right", padding: "5px 14px", fontWeight: 800,
                            color: groupTotals.variance < 0 ? "oklch(0.48 0.20 25)" : "oklch(0.38 0.15 155)",
                          }}>{fmt(groupTotals.variance)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: "oklch(0.22 0.04 250)", borderTop: "2px solid oklch(0.18 0.03 250)" }}>
                  <td style={{ padding: "8px 14px", fontWeight: 800, color: "oklch(0.92 0.01 250)", fontSize: 10, textTransform: "uppercase" as const, letterSpacing: "0.06em" }}>Total</td>
                  <td style={{ textAlign: "right", padding: "8px 14px", fontWeight: 700, color: "oklch(0.88 0.01 250)" }}>{fmt(totals.annualBudget)}</td>
                  <td style={{ textAlign: "right", padding: "8px 14px", fontWeight: 800, color: "oklch(0.95 0.01 250)" }}>{fmt(totals.mpy)}</td>
                  <td style={{
                    textAlign: "right", padding: "8px 14px", fontWeight: 800,
                    color: totals.variance < 0 ? "oklch(0.70 0.18 25)" : "oklch(0.70 0.18 155)",
                  }}>{fmt(totals.variance)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default YTGSimulationStrip;
